import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, chainColor,
	FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'

// Что показывает ячейка матрицы
const CELL_MODES = ['amount', 'revenue_percent', 'branch_share']

function Expenses() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [cellMode, setCellMode] = useState('amount')
	const [search, setSearch] = useState('')
	const [details, setDetails] = useState(null)

	const filters = useChainFilters({ purpose_ids: [], payment_type_ids: [], kinds: ['expense'] }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions(['expense_purposes', 'payment_types'])

	const kindOptions = [
		{ id: 'expense', name: t('chain_kind_expense') },
		{ id: 'supplier', name: t('chain_kind_supplier') },
		{ id: 'staff', name: t('chain_kind_staff') },
	]
	const purposeOptions = useMemo(() => [{ id: 0, name: t('chain_no_purpose') }, ...options.expensePurposes], [options.expensePurposes, t])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/expenses', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const branchById = useMemo(() => Object.fromEntries(branches.map(item => [item.pos_id, item])), [branches])

	// Значение ячейки в выбранном режиме
	function cellValue(purpose, posId) {
		const amount = Number(purpose.poses?.[posId] || 0)
		if (cellMode === 'amount') return amount
		const branch = branchById[posId]
		if (cellMode === 'revenue_percent') return branch?.revenue > 0 ? amount / branch.revenue * 100 : null
		return branch?.amount > 0 ? amount / branch.amount * 100 : null
	}

	function formatCell(value) {
		if (value === null || value === undefined) return '—'
		if (cellMode === 'amount') return value > 0 ? money(value) : '—'
		return value > 0 ? percent(value) : '—'
	}

	const purposes = useMemo(() => {
		const list = data?.purposes || []
		const query = search.trim().toLowerCase()
		return query ? list.filter(item => item.purpose_name.toLowerCase().includes(query)) : list
	}, [data, search])

	// Колонка филиала сортируется по тому, что сейчас в ячейке (сумма или процент)
	const readSortValue = (row, key) => key.startsWith('pos:') ? cellValue(row, Number(key.slice(4))) : row[key]
	const { sorted, sort, toggle } = useSort(purposes, 'amount', 'desc', readSortValue)
	const branchStats = useMemo(() => ({
		amount: columnStats(branches, 'amount'),
		revenue_percent: columnStats(branches, 'revenue_percent'),
	}), [branches])

	const topPurpose = (data?.purposes || [])[0]
	const topSpender = branches.length > 0 ? [...branches].sort((a, b) => b.amount - a.amount)[0] : null
	const withRevenue = branches.filter(item => item.revenue_percent !== null && item.amount > 0)
	const leanest = withRevenue.length > 1 ? [...withRevenue].sort((a, b) => a.revenue_percent - b.revenue_percent)[0] : null

	async function openDetails(posId, purpose) {
		const response = await GET(buildAnalyticsUrl('/chain/report/expenses-details', applied, {
			pos_id: posId || null,
			purpose_id: purpose ? purpose.purpose_id : null,
		}), {}, { loader: true })
		if (httpOk(response)) {
			setDetails({
				...response.data,
				pos_name: posId ? branchById[posId]?.pos_name : t('chain_all_branches'),
				purpose_name: purpose ? purpose.purpose_name : t('chain_all_purposes'),
			})
		}
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const purposeRows = data.purposes || []
		downloadExcel(`${t('chain_expenses_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('chain_by_purposes'),
				title: t('chain_expenses_title'),
				subtitle: period,
				columns: [
					{ label: t('chain_purpose'), type: 'text', width: 34 },
					...branches.map(branch => ({ label: branch.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
				],
				rows: purposeRows.map(purpose => [
					purpose.purpose_name,
					...branches.map(branch => Number(purpose.poses?.[branch.pos_id] || 0)),
					purpose.amount,
					purpose.share,
				]),
				footer: [t('total'), ...branches.map(branch => branch.amount), totals.amount, 100],
			},
			{
				name: t('chain_branches'),
				title: `${t('chain_expenses_title')} — ${t('chain_branches')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('expenses'), type: 'money' },
					{ label: t('chain_operations'), type: 'int', width: 11 },
					{ label: t('revenue'), type: 'money' },
					{ label: t('chain_percent_of_revenue'), type: 'percent', width: 14 },
					{ label: t('chain_kind_expense'), type: 'money' },
					{ label: t('chain_kind_supplier'), type: 'money' },
					{ label: t('chain_kind_staff'), type: 'money' },
				],
				rows: [...branches].sort((a, b) => b.amount - a.amount).map(branch => [
					branch.pos_name, branch.amount, branch.quantity, branch.revenue, branch.revenue_percent,
					branch.by_kind.expense, branch.by_kind.supplier, branch.by_kind.staff,
				]),
				footer: [t('total'), totals.amount, totals.quantity, totals.revenue, totals.revenue_percent,
					totals.by_kind?.expense, totals.by_kind?.supplier, totals.by_kind?.staff],
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('chain_expenses_title')} — ${details.purpose_name} — ${details.pos_name}`, [{
			name: t('chain_operations'),
			title: `${details.purpose_name} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: [
				{ label: t('date'), type: 'text', width: 18 },
				{ label: t('chain_branch'), type: 'text', width: 24 },
				{ label: t('chain_purpose'), type: 'text', width: 28 },
				{ label: t('payment_type'), type: 'text', width: 18 },
				{ label: t('cashier'), type: 'text', width: 16 },
				{ label: t('note'), type: 'text', width: 36 },
				{ label: t('amount'), type: 'money' },
			],
			rows: details.rows.map(row => [
				formatDate(row.created_at, 'dd.MM.yyyy HH:mm'), row.pos_name, row.purpose_name, row.payment_type_name,
				row.cashier_login || '', row.note || '', row.amount,
			]),
			footer: [t('total'), '', '', '', '', '', details.total],
		}])
	}

	const modeLabels = {
		amount: t('chain_mode_amount'),
		revenue_percent: t('chain_mode_revenue_percent'),
		branch_share: t('chain_mode_branch_share'),
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_expenses_title')}
				icon="uil-money-withdraw"
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<FilterField label={t('chain_purposes')}>
					<FilterSelect isMulti options={purposeOptions} allLabel={t('chain_all_purposes')} value={draft.purpose_ids} onChange={(ids) => patch({ purpose_ids: ids })} />
				</FilterField>
				<FilterField label={t('payment_type')}>
					<FilterSelect isMulti options={options.paymentTypes} allLabel={t('chain_all_payment_types')} value={draft.payment_type_ids} onChange={(ids) => patch({ payment_type_ids: ids })} />
				</FilterField>
				<FilterField label={t('chain_expense_kinds')}>
					<FilterSelect isMulti options={kindOptions} allLabel={t('chain_all_kinds')} value={draft.kinds} onChange={(ids) => patch({ kinds: ids })} />
				</FilterField>
			</ChainFilterBar>

			{(applied.kinds || []).length === 1 && applied.kinds[0] === 'expense' && (
				<div className="chain-note mb-3">
					<i className="uil uil-info-circle"></i>
					<span>{t('chain_expenses_kind_note')}</span>
				</div>
			)}

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_total_expenses')} icon="uil-money-withdraw" color="#f06548"
						value={money(totals.amount)}
						sub={<>{formatMoney(totals.quantity)} {t('chain_operations_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_percent_of_revenue')} icon="uil-percentage" color="#f59f00"
						value={percent(totals.revenue_percent)}
						sub={<>{t('revenue')} {money(totals.revenue)}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_top_spender')} icon="uil-fire" color="#f06548"
						value={topSpender && topSpender.amount > 0 ? topSpender.pos_name : '—'}
						sub={topSpender && topSpender.amount > 0 ? <>{money(topSpender.amount)} · {percent(topSpender.revenue_percent)} {t('chain_of_revenue')}</> : null} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_top_purpose')} icon="uil-tag-alt" color="#5b73e8"
						value={topPurpose ? topPurpose.purpose_name : '—'}
						sub={topPurpose ? <>{money(topPurpose.amount)} · {percent(topPurpose.share)}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_by_purposes')}</h5>
							<FilterSegment
								options={CELL_MODES.map(id => ({ id, label: modeLabels[id] }))}
								value={cellMode}
								onChange={setCellMode}
							/>
						</div>
						<div className="chain-toolbar-group">
							<HeatLegend mode="danger" />
							<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_purpose')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="purpose_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_purpose')}</SortTh>
									{branches.map((branch, index) => (
										<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={sort} onSort={toggle} className="text-end">
											<span className="chain-dot" style={{ background: chainColor(index) }}></span>
											{branch.pos_name}
										</SortTh>
									))}
									<SortTh sortKey="amount" sort={sort} onSort={toggle} className="text-end chain-total-col">{t('total')}</SortTh>
									<SortTh sortKey="share" sort={sort} onSort={toggle} className="text-end">{t('share')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={branches.length + 4} />}
								{sorted.map((purpose, rowIndex) => {
									const values = branches.map(branch => cellValue(purpose, branch.pos_id))
									const rowMax = Math.max(0, ...values.map(value => Number(value || 0)))
									const onlyKind = purpose.kinds?.length === 1 && purpose.kinds[0] !== 'expense' ? purpose.kinds[0] : null
									return (
										<tr key={purpose.purpose_id}>
											<td className="chain-index chain-sticky">{rowIndex + 1}</td>
											<td className="chain-name chain-sticky-2 chain-click" onClick={() => openDetails(null, purpose)} title={t('chain_open_details')}>
												<span>{purpose.purpose_name}</span>
												{onlyKind && <div className="chain-muted">{t(`chain_kind_${onlyKind}`)}</div>}
											</td>
											{branches.map((branch, index) => {
												const value = values[index]
												const isMax = value > 0 && value === rowMax && branches.length > 1
												return (
													<td key={branch.pos_id}
														className={`chain-num ${value > 0 ? 'chain-click' : ''}`}
														style={intensityStyle(value, rowMax, 'danger')}
														title={value > 0 ? `${branch.pos_name}: ${money(purpose.poses[branch.pos_id])} · ${formatMoney(purpose.counts?.[branch.pos_id] || 0)} ${t('chain_operations_lower')}` : undefined}
														onClick={() => value > 0 && openDetails(branch.pos_id, purpose)}>
														<span style={{ fontWeight: isMax ? 800 : 400 }}>{formatCell(value)}</span>
													</td>
												)
											})}
											<td className="chain-num chain-total-col">{money(purpose.amount)}</td>
											<FillCell percent={purpose.share} tone="danger">{percent(purpose.share)}</FillCell>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_total_expenses')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num chain-click" style={heatStyle(branch.amount, branchStats.amount, false)}
												onClick={() => branch.amount > 0 && openDetails(branch.pos_id, null)}>
												{money(branch.amount)}
											</td>
										))}
										<td className="chain-num chain-total-col">{money(totals.amount)}</td>
										<td className="chain-num">100%</td>
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('revenue')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num">{money(branch.revenue)}</td>
										))}
										<td className="chain-num chain-total-col">{money(totals.revenue)}</td>
										<td></td>
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2" title={t('chain_percent_of_revenue_hint')}>{t('chain_percent_of_revenue')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={heatStyle(branch.revenue_percent, branchStats.revenue_percent, false)}>
												{percent(branch.revenue_percent)}
											</td>
										))}
										<td className="chain-num chain-total-col">{percent(totals.revenue_percent)}</td>
										<td></td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_expenses_matrix_hint')}
						{leanest && <> · {t('chain_leanest_branch')}: <b>{leanest.pos_name}</b> ({percent(leanest.revenue_percent)} {t('chain_of_revenue')})</>}
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className={(data?.monthly?.months || []).length > 1 ? 'col-xl-7' : 'col-12'}>
					<StructureChart branches={branches} purposes={data?.purposes || []} />
				</div>
				{(data?.monthly?.months || []).length > 1 && (
					<div className="col-xl-5">
						<MonthlyChart monthly={data.monthly} />
					</div>
				)}
			</div>

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.purpose_name} — {details?.pos_name}
						<div className="chain-muted fw-normal">{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_operations_lower')} · {money(details?.total)}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('date')}</th>
								<th>{t('chain_branch')}</th>
								<th>{t('chain_purpose')}</th>
								<th>{t('payment_type')}</th>
								<th>{t('cashier')}</th>
								<th>{t('note')}</th>
								<th className="text-end">{t('amount')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={8} />}
							{(details?.rows || []).map((row, index) => (
								<tr key={row.id}>
									<td className="chain-index">{index + 1}</td>
									<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
									<td>{row.pos_name}</td>
									<td>
										{row.purpose_name}
										{row.kind !== 'expense' && <div className="chain-muted">{t(`chain_kind_${row.kind}`)}</div>}
									</td>
									<td>{row.payment_type_name}</td>
									<td>{row.cashier_login || '—'}</td>
									<td style={{ maxWidth: '280px' }}>{row.note || '—'}</td>
									<td className="chain-num"><b>{money(row.amount)}</b></td>
								</tr>
							))}
						</tbody>
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

// Структура расходов филиалов: крупные статьи стопкой, остальное — «Прочее»
function StructureChart({ branches, purposes }) {
	const { t } = useTranslation()
	const TOP = 6
	const top = purposes.slice(0, TOP)
	const rest = purposes.slice(TOP)
	const rows = [...branches].sort((a, b) => b.amount - a.amount)

	const series = top.map(purpose => ({
		name: purpose.purpose_name,
		data: rows.map(branch => Math.round(Number(purpose.poses?.[branch.pos_id] || 0))),
	}))
	if (rest.length > 0) {
		series.push({
			name: t('chain_other'),
			data: rows.map(branch => Math.round(rest.reduce((sum, purpose) => sum + Number(purpose.poses?.[branch.pos_id] || 0), 0))),
		})
	}

	const options = {
		chart: { type: 'bar', stacked: true, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '62%' } },
		colors: ['#f06548', '#f59f00', '#5b73e8', '#7c4dff', '#12b886', '#299cdb', '#adb5bd'],
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(branch => branch.pos_name), labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: { y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_expense_structure')}</h5></div>
				{rows.length > 0 && <ReactApexChart type="bar" height={Math.max(300, rows.length * 40 + 90)} series={series} options={options} />}
			</div>
		</div>
	)
}

// Расходы филиалов по месяцам — растут или нет
function MonthlyChart({ monthly }) {
	const { t } = useTranslation()
	const series = monthly.series.map(item => ({ name: item.pos_name, data: item.data.map(value => Math.round(value)) }))
	const options = {
		chart: { type: 'bar', toolbar: { show: false } },
		plotOptions: { bar: { columnWidth: '70%', borderRadius: 2 } },
		colors: monthly.series.map((item, index) => chainColor(index)),
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: monthly.months.map(month => month.slice(5, 7) + '.' + month.slice(0, 4)) },
		yaxis: { labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: { y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_by_months')}</h5></div>
				<ReactApexChart type="bar" height={340} series={series} options={options} />
			</div>
		</div>
	)
}

export default Expenses
